import React from 'react'

import { OmTabs } from '@owma/kit'

import i18n from 'i18n'
import classNames from 'classnames'
import { EmptyData } from 'utils/ui'

import RecentEmailList from 'popups/component/RecentEmailList'
import OrgChartTree from 'pages/contacts/components/OrgChart/Tree'
import Search from 'components/Search'

import SharedContactList from 'pages/contacts/components/SharedContacts/ContactList'
import PersonalContactList from 'pages/contacts/components/PersonalContacts/ContactList'

import iconClear20SVG from 'assets/images/icon-search-clear.svg'
import styles from './style.module.scss'
import { getAppServices } from 'env'

interface UserInfo {
  email: string
  name?: string
  display_name?: string
  profile_photo_url?: string
  avatarUrl?: string
}

interface SelectUserProps {
  userOnly?: boolean
  defaultEmailList?: string[]
  excludeEmailList?: string[]
  disableEmails?: string[]
  appendUserTitles?: string[]
  onUserSelectChanged?: (emailList: string[], userList: UserInfo[]) => void
}

interface SelectUserState {
  activeKey: string
}

interface UserListInfo {
  emailList: string[]
  userList: UserInfo[]
}

class SelectUser extends React.Component<SelectUserProps, SelectUserState> {
  state: SelectUserState = {
    activeKey: 'organization',
  }

  emailSearchRef = React.createRef<any>()
  sharedContactSearchRef = React.createRef<any>()
  personalContactSearchRef = React.createRef<any>()

  appServices = getAppServices()
  disableSharedContacts: boolean =
    this.appServices.can_use_email === false && this.appServices.pages.contacts === false

  recentInfo: UserListInfo = {
    emailList: [],
    userList: [],
  }

  orgChartInfo: UserListInfo = {
    emailList: [],
    userList: [],
  }

  sharedContactInfo: UserListInfo = {
    emailList: [],
    userList: [],
  }

  personalContactInfo: UserListInfo = {
    emailList: [],
    userList: [],
  }

  activeKey?: string

  componentDidMount(): void {}

  componentDidUpdate(): void {}

  componentWillUnmount(): void {}

  notifyUserSelectChanged = (): void => {
    const { onUserSelectChanged } = this.props

    const mergedUserList: UserInfo[] = [...this.recentInfo.userList]

    this.orgChartInfo.userList.forEach((user) => {
      const existUser = mergedUserList.find((mUser) => {
        return mUser.email === user.email
      })
      if (existUser == null) {
        // orgChart에는 name과 avatarUrl이 없으므로 데이터 구조 통합을 위해 name과 avatarUrl을 추가함.
        user.name = user.display_name
        user.avatarUrl = user.profile_photo_url
        mergedUserList.push(user)
      }
    })

    this.sharedContactInfo.userList.forEach((user) => {
      const existUser = mergedUserList.find((mUser) => {
        return mUser.email === user.email
      })
      if (existUser == null) {
        mergedUserList.push(user)
      }
    })

    this.personalContactInfo.userList.forEach((user) => {
      const existUser = mergedUserList.find((mUser) => {
        return mUser.email === user.email
      })
      if (existUser == null) {
        mergedUserList.push(user)
      }
    })

    const mergedEmailList = mergedUserList.map((user) => {
      return user.email
    })

    if (onUserSelectChanged != null) {
      onUserSelectChanged(mergedEmailList, mergedUserList)
    }
  }

  onRecentChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    console.log('onRecentChange - ', e)
    if (this.emailSearchRef.current != null) {
      this.emailSearchRef.current.onChange(e)
    }
  }

  onRecentSearch = (e: string): void => {
    console.log('onRecentSearch - ', e)
    if (this.emailSearchRef.current != null) {
      this.emailSearchRef.current.onSearch(e)
    }
  }

  renderRecent = (): React.ReactElement => {
    const { defaultEmailList, excludeEmailList, disableEmails } = this.props

    const attrs = {
      innerRef: this.emailSearchRef,
      size: 'small' as const,
      defaultEmailList,
      excludeEmailList,
      disableEmails,
      locale: {
        emptyText: <EmptyData type="invitation" mailbox={null} />,
      },
      onSelectChanged: (changedEmailList: string[], changedUserList: UserInfo[]) => {
        this.recentInfo.emailList = changedEmailList
        this.recentInfo.userList = changedUserList

        this.notifyUserSelectChanged()
      },
    }

    const searchAttrs: any = {
      placeholder: i18n.t('email.placeholder.search'),
      onSearch: this.onRecentSearch,
      allowClear: { clearIcon: <img src={iconClear20SVG} alt="clear" /> },
      onChange: this.onRecentChange,
      className: styles.search,
    }

    return (
      <div className={classNames(styles.sharedContactsWrapper)}>
        <Search {...searchAttrs} /> <RecentEmailList {...attrs} />
      </div>
    )
  }

  renderOrgChart = (): React.ReactElement => {
    const { defaultEmailList, excludeEmailList, disableEmails, appendUserTitles } = this.props

    const attrs = {
      enableCheck: true,
      defaultEmailList,
      excludeEmailList,
      disableEmails,
      appendUserTitles,
      onSelectChanged: (changedEmailList: string[], changedUserList: UserInfo[]) => {
        this.orgChartInfo.emailList = changedEmailList
        this.orgChartInfo.userList = changedUserList

        this.notifyUserSelectChanged()
      },
      searchClassName: 'only-mail-orgchart-search',
      orgChartTreeClassName: 'mailboxOrgChartTree',
    }

    return <OrgChartTree {...(attrs as any)} />
  }

  onSharedContactChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    console.log('onSharedContactChange - ', e)
    if (this.sharedContactSearchRef.current != null) {
      this.sharedContactSearchRef.current.onChange(e)
    }
  }

  onSharedContactSearch = (e: string): void => {
    console.log('onSharedContactSearch - ', e)
    if (this.sharedContactSearchRef.current != null) {
      this.sharedContactSearchRef.current.onSearch(e)
    }
  }

  renderSharedContacts = (): React.ReactElement => {
    const { defaultEmailList, excludeEmailList, disableEmails } = this.props

    const attrs = {
      innerRef: this.sharedContactSearchRef,
      enableCheck: true,
      emailMode: true,
      size: 'small' as const,
      defaultEmailList,
      excludeEmailList,
      disableEmails,
      onSelectChanged: (changedEmailList: string[], changedUserList: UserInfo[]) => {
        this.sharedContactInfo.emailList = changedEmailList
        this.sharedContactInfo.userList = changedUserList

        this.notifyUserSelectChanged()
      },
    }

    const onChangeBack = (e: any): void => {
      console.log('onChangeBack - ', e)
      this.onSharedContactSearch('')
    }

    const searchAttrs: any = {
      placeholder: i18n.t('email.placeholder.search'),
      onSearch: this.onSharedContactSearch,
      allowClear: { clearIcon: <img src={iconClear20SVG} alt="clear" /> },
      onChange: this.onSharedContactChange,
      className: styles.search,
      onChangeBack,
    }

    return (
      <div className={classNames(styles.sharedContactsWrapper)}>
        <Search {...searchAttrs} />
        <SharedContactList {...(attrs as any)} />
      </div>
    )
  }

  onPersonalContactChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    console.log('onPersonalContactChange - ', e)
    if (this.personalContactSearchRef.current != null) {
      this.personalContactSearchRef.current.onChange(e)
    }
  }

  onPersonalContactSearch = (searchText: string): void => {
    console.log('onPersonalContactSearch - ', searchText)
    if (this.personalContactSearchRef.current != null) {
      this.personalContactSearchRef.current.onSearch(searchText)
    }
  }

  renderPersonalContacts = (): React.ReactElement => {
    const { defaultEmailList, excludeEmailList, disableEmails } = this.props

    const searchAttrs: any = {
      placeholder: i18n.t('email.placeholder.search'),
      onSearch: this.onPersonalContactSearch,
      allowClear: { clearIcon: <img src={iconClear20SVG} alt="clear" /> },
      onChange: this.onPersonalContactChange,
      className: styles.search,
      onChangeBack: () => this.onPersonalContactSearch(''),
    }

    return (
      <div className={classNames(styles.sharedContactsWrapper)}>
        <Search {...searchAttrs} />
        <PersonalContactList
          innerRef={this.personalContactSearchRef}
          pickerMode={true}
          defaultEmailList={defaultEmailList}
          excludeEmailList={excludeEmailList}
          disableEmails={disableEmails}
          onSelectChanged={(changedEmailList: string[], changedUserList: UserInfo[]) => {
            this.personalContactInfo.emailList = changedEmailList
            this.personalContactInfo.userList = changedUserList

            this.notifyUserSelectChanged()
          }}
        />
      </div>
    )
  }

  getTabItems = (): any[] => {
    const items = [
      {
        key: 'recent',
        label: i18n.t('mailbox.moveTab.recents'),
        children: this.renderRecent(),
      },
      {
        key: 'organization',
        label: i18n.t('email.tab.organization'),
        children: this.renderOrgChart(),
      },
    ]

    if (this.disableSharedContacts === false) {
      items.push({
        key: 'sharedContacts',
        label: i18n.t('email.tab.sharedContacts'),
        children: this.renderSharedContacts(),
      })

      items.push({
        key: 'personalContacts',
        label: i18n.t('contact.title.personalContacts'),
        children: this.renderPersonalContacts(),
      })
    }

    return items
  }

  render(): React.ReactElement {
    const { userOnly } = this.props

    let elem: React.ReactElement | null = null

    if (userOnly === false) {
      const { activeKey } = this.state
      const items = this.getTabItems()

      const tabsAttrs = {
        className: classNames('custom-tabs-nav'),
        items,
        activeKey,
        centered: true,
        onChange: (newActiveKey: string) => {
          this.activeKey = newActiveKey

          this.setState({
            activeKey: this.activeKey,
          })
        },
      }
      elem = <OmTabs {...tabsAttrs} />
    } else {
      elem = this.renderOrgChart()
    }

    return <div className={classNames(styles.SelectUser)}>{elem}</div>
  }
}

export default SelectUser
